import { FC, ReactNode, useState } from 'react'
import React from 'react'
import './Navigation.less'
import { ConfigProvider, Drawer, Tabs, TabsProps } from 'antd'
import { isMobile } from 'react-device-detect'
import { Menu } from 'antd'
import { Logo } from '../../atoms/Logo'
import { theme } from '../../config/theme'
import IconType from '../../general/Icon/iconTypes'
import Icon from '../../general/Icon'
import styled from 'styled-components'

const { SubMenu } = Menu

type Tab = {
  label: string
  key: string
  subTabs?: {
    label: string
    key?: string
    id?: string
    disabledByPermission?: boolean
  }[]
  action?: () => void
}

export type NavigationProps = {
  tabs: Tab[]
  rightChild?: ReactNode
  activeKey?: string
  router?: any
  allTabsInactive?: boolean
  withSubNav?: boolean
}

type TabsStyledProps = {
  $withSubNav?: boolean
} & TabsProps

const TabsStyled = styled(Tabs)<TabsStyledProps>`
  .ant-tabs-tab-active {
    background-color: ${(props) => props.theme.color.background.secondary};
    height: ${(props) => (props.$withSubNav ? '55px' : '40px')};
    margin-top: auto !important;
    margin-bottom: ${(props) =>
      props.$withSubNav ? '0px' : 'auto'} !important;
    border-radius: 8px;
    padding: 8px 16px;
    border-bottom-right-radius: ${(props) =>
      props.$withSubNav ? '0px' : '8px'};
    border-bottom-left-radius: ${(props) =>
      props.$withSubNav ? '0px' : '8px'};
    padding-bottom: ${(props) =>
      props.$withSubNav ? '15px !important' : '8px !important'};
  }

  .ant-tabs-tab {
    padding: 8px 16px;
    margin-left: 0px !important;
  }

  .ant-tabs-nav-operations {
    display: none !important;
  }
`

const Navigation = ({
  tabs,
  rightChild,
  activeKey,
  router,
  allTabsInactive,
  withSubNav = false,
}: NavigationProps) => {
  const [sideMenuOpen, setSideMenuOpen] = useState(false)

  const styles = {
    wrapper: {
      backgroundColor: theme.palette.darkpurple,
      display: 'flex',
      alignItems: 'center',
      height: 63,
      ...(isMobile && { justifyContent: 'space-between' }),
    },
    rightChild: {
      marginLeft: 'auto',
    },
    iconLogs: {
      fill: theme.palette.black,
      width: '24px',
      height: '25px',
      marginLeft: 24,
    },
    iconHamburger: {
      fill: theme.palette.black,
      width: '24px',
      height: '25px',
      marginLeft: 24,
    },
    iconShare: {
      fill: theme.palette.black,
      width: '24px',
      height: '25px',
      marginRight: 24,
    },
    drawer: { paddingTop: '63px', height: 'calc(100vh - 63px)' },
  }

  const rootSubmenuKeys = tabs.map((item) => item.key)

  const [openKeys, setOpenKeys] = useState<React.Key[]>([])

  const onOpenChange = (keys: React.Key[]) => {
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1)
    if (rootSubmenuKeys.indexOf(latestOpenKey as string) === -1) {
      setOpenKeys(keys)
    } else {
      setOpenKeys(latestOpenKey ? [latestOpenKey] : [])
    }
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: theme.palette.pink,
        },
      }}
    >
      <div id="antd-navigation">
        <div
          style={styles.wrapper}
          id={allTabsInactive ? 'all-tabs-inactive' : ''}
        >
          {isMobile && (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {!sideMenuOpen ? (
                <Icon
                  customIcon={IconType.Logs}
                  style={styles.iconLogs}
                  onClick={() => setSideMenuOpen(true)}
                />
              ) : (
                <Icon
                  customIcon={IconType.Hamburger}
                  style={styles.iconHamburger}
                  onClick={() => setSideMenuOpen(false)}
                />
              )}
            </>
          )}
          <div onClick={() => router.push('/')}>
            <Logo type="light" />
          </div>
          {!isMobile && (
            <>
              <TabsStyled
                onChange={(value) => {
                  router.push(`/${value}`)
                }}
                activeKey={activeKey}
                $withSubNav={withSubNav}
                items={tabs.map((tab) => ({
                  key: tab.key,
                  label: tab.label,
                  children: <div data-cy={tab.key} />,
                }))}
              />

              {rightChild && <div style={styles.rightChild}>{rightChild}</div>}
            </>
          )}
          {isMobile && (
            <Icon customIcon={IconType.Share} style={styles.iconShare} />
          )}
          <Drawer
            placement="left"
            style={styles.drawer}
            onClose={() => setSideMenuOpen(false)}
            open={sideMenuOpen}
            width="260px"
            className="antd-mobile-tabs"
          >
            {/* @ts-ignore */}
            <Menu
              mode="inline"
              // @ts-ignore
              openKeys={openKeys}
              onOpenChange={onOpenChange}
              // @ts-ignore
              selectedKeys={openKeys}
            >
              {tabs.map((tab) => {
                const hasSubItem = tab?.subTabs?.length
                if (!hasSubItem)
                  return (
                    <Menu.Item
                      key={tab.key}
                      onClick={() => {
                        // eslint-disable-next-line no-unused-expressions
                        tab?.action
                          ? tab?.action()
                          : router?.push(`/${tab.key}`)
                        setSideMenuOpen(false)
                      }}
                    >
                      {tab.label}
                    </Menu.Item>
                  )
                return (
                  <SubMenu key={tab.key} title={tab.label}>
                    {tab.subTabs?.map((subTab) => {
                      const isResidentRoute =
                        (tab.key === 'residents' || tab.key === 'homes') &&
                        router?.params?.id
                      if (subTab.disabledByPermission) {
                        return null
                      }
                      return (
                        <Menu.Item
                          key={subTab.key}
                          onClick={() => {
                            router?.push(
                              `/${tab.key}/${
                                isResidentRoute ? router.params.id : ''
                              }${isResidentRoute ? '/' : ''}${subTab.key}`
                            )
                            setSideMenuOpen(false)
                          }}
                        >
                          {subTab.label}
                        </Menu.Item>
                      )
                    })}
                  </SubMenu>
                )
              })}
            </Menu>
            ,
          </Drawer>
        </div>
      </div>
    </ConfigProvider>
  )
}

export default Navigation
